import { EC2Server, OutputEc2 } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Grid } from '@amzn/awsui-components-react';

import { constants } from '../../common/Localization';
import { formatPreInstalledSw } from '../../common/LocalizationHelper';
import { ValueWithLabel } from './utils';

type Props = {
    tcoAssessmentId: string | undefined;
    awsEC2result: OutputEc2 | undefined;
    server: EC2Server | undefined;
}

/**
 * Displays the grid with key value pairs for an AWS EC2 recommendation
 * Used when a selected server or selected database meets specific requirements
 * for databases it used when evaluated as EC2 with SQL server
 */
export function AwsEC2RecommendationDetail({tcoAssessmentId, awsEC2result, server}: Props): JSX.Element | null {
    if (!awsEC2result || !server) {
        return null;
    } 
    let vCpu = '';
    let memory = '';

    const lowestOffer: string = awsEC2result.pricing.offerDetails[server.lowestOfferName];
    const instance = awsEC2result.instanceTypes.find(type => type.instanceType === server.instanceType);
    if (instance) {
        vCpu = instance.vcpu;
        memory = instance.memory;
    }

    if (tcoAssessmentId) {
        if (server.recommendedEbsOffer) {
            const volumeType = (server.recommendedEbsOffer.volumeType);
            const volumeSize = (server.recommendedEbsOffer.diskSize);
            const maxIopsVolume = (server.recommendedEbsOffer.maxIopsVolume);
            const maxThroughputVolume = (server.recommendedEbsOffer.maxThroughputVolume);
            return (
                <Grid
                    data-testid='awsEC2Recommendations'
                    gridDefinition={[{colspan: 6}, {colspan: 6}, {colspan: 6}, {colspan: 6}, {colspan: 12}, {colspan: 12}, {colspan: 12}]}
                >
                    <ValueWithLabel label={constants.AWS_RECOMMENDATION}>{constants.EC2}</ValueWithLabel>
                    <ValueWithLabel label={constants.TENANCY}>{server.tenancy}</ValueWithLabel>
                    <ValueWithLabel label={constants.RECOMMENDED_PAYMENT_OPTION}>{lowestOffer}</ValueWithLabel>
                    <ValueWithLabel label={constants.PREINSTALLED_SOFTWARE}>{formatPreInstalledSw(server.preInstalledSw)}</ValueWithLabel>
                    <ValueWithLabel label={constants.OS}>{server.operatingSystem}</ValueWithLabel>
                    <ValueWithLabel
                        label={constants.INSTANCE}>{`${server.instanceType} / ${vCpu} ${constants.V_CPUS} / ${memory} ${constants.RAM}`}
                    </ValueWithLabel>
                    <ValueWithLabel label={constants.VOLUME}>
                        {`${constants.EBS} ${volumeType} / ${volumeSize} GB / ${maxIopsVolume}`
                        + ` ${constants.MAX_IOPS} / ${maxThroughputVolume} MB/s ${constants.MAX_THROUGHPUT}`}
                    </ValueWithLabel>
                </Grid>
            );
        } else { 
            return (
                <Box>
                    {constants.EC2_RECOMM_NOT_MADE_FOR_SERVER}
                </Box>
            );
        }
    } else {
        return (
            <Box>
                <Box variant='strong'>
                    {constants.AWS_RECOMMENDATION_NOT_COMPUTED}
                </Box>
                <Box>
                    {constants.RETURN_TO_TCO_AND_CALCULATE}
                </Box>
            </Box>
        );
    }
};
